import { useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import Pagination from "../ui/pagination";
import { Loader2, Search, RefreshCw } from "lucide-react";
import api from "../../lib/api";
import { useToast } from "../../hooks/use-toast";

export default function ChangeLogTab() {
	const [loading, setLoading] = useState(false);
	const [logs, setLogs] = useState<any[]>([]);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);
	const [total, setTotal] = useState(0);
	const [search, setSearch] = useState("");
	const [changeType, setChangeType] = useState("all");
	const [changedBy, setChangedBy] = useState("all");
	const [dateFrom, setDateFrom] = useState("");
	const [dateTo, setDateTo] = useState("");
	const [filterOptions, setFilterOptions] = useState<{ change_types: string[]; changed_by_users: string[] }>({
		change_types: [],
		changed_by_users: [],
	});
	const { toast } = useToast();

	useEffect(() => {
		fetchLogs();
	}, [page, changeType, changedBy, dateFrom, dateTo]);

	useEffect(() => {
		const timer = setTimeout(() => {
			setPage(1);
			fetchLogs();
		}, 400);
		return () => clearTimeout(timer);
	}, [search]);

	const fetchLogs = async () => {
		setLoading(true);
		try {
			const params: Record<string, any> = { page, limit: 50 };
			if (search.trim()) params.search = search.trim();
			if (changeType && changeType !== "all") params.change_type = changeType;
			if (changedBy && changedBy !== "all") params.changed_by = changedBy;
			if (dateFrom) params.date_from = dateFrom;
			if (dateTo) params.date_to = dateTo;

			const res = await api.b2cMgmtGetChangeLog(params);
			if (res?.success) {
				setLogs(res.data || []);
				setTotal(res.pagination?.total || 0);
				setTotalPages(res.pagination?.totalPages || 0);
				if (res.filters) {
					setFilterOptions(res.filters);
				}
			}
		} catch (error: any) {
			toast({ title: "Error", description: "Failed to load change log", variant: "destructive" });
		} finally {
			setLoading(false);
		}
	};

	const changeTypeBadgeColor = (type: string) => {
		switch (type) {
			case "flag_change": return "default";
			case "price_correction": return "destructive";
			case "name_correction": return "secondary";
			case "packing_correction": return "secondary";
			case "category_change": return "outline";
			case "tag_change": return "outline";
			case "duplicate_resolution": return "default";
			default: return "secondary";
		}
	};

	return (
		<div className="space-y-4">
			{/* Filters */}
			<Card className="dark:bg-gray-800 dark:border-gray-700">
				<CardContent className="p-4">
					<div className="flex flex-wrap items-center gap-3">
						<div className="relative flex-1 min-w-[200px]">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
							<Input
								placeholder="Search field, old/new value..."
								className="pl-9 dark:bg-gray-700 dark:border-gray-600"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
							/>
						</div>
						<Select value={changeType} onValueChange={setChangeType}>
							<SelectTrigger className="w-[180px] dark:bg-gray-700 dark:border-gray-600">
								<SelectValue placeholder="Change type" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All types</SelectItem>
								{filterOptions.change_types.map((t) => (
									<SelectItem key={t} value={t}>{t}</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Select value={changedBy} onValueChange={setChangedBy}>
							<SelectTrigger className="w-[200px] dark:bg-gray-700 dark:border-gray-600">
								<SelectValue placeholder="Changed by" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All users</SelectItem>
								{filterOptions.changed_by_users.map((u) => (
									<SelectItem key={u} value={u}>{u}</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Input
							type="date"
							value={dateFrom}
							onChange={(e) => setDateFrom(e.target.value)}
							className="w-[150px] dark:bg-gray-700 dark:border-gray-600"
							placeholder="From"
						/>
						<Input
							type="date"
							value={dateTo}
							onChange={(e) => setDateTo(e.target.value)}
							className="w-[150px] dark:bg-gray-700 dark:border-gray-600"
							placeholder="To"
						/>
						<Button variant="outline" size="sm" onClick={fetchLogs} className="dark:bg-gray-700 dark:border-gray-600">
							<RefreshCw className="h-4 w-4" />
						</Button>
					</div>
					<div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
						{total.toLocaleString()} log entries
					</div>
				</CardContent>
			</Card>

			{/* Log Table */}
			<Card className="dark:bg-gray-800 dark:border-gray-700">
				<CardContent className="p-0">
					{loading ? (
						<div className="flex items-center justify-center h-40">
							<Loader2 className="h-6 w-6 animate-spin text-blue-500" />
						</div>
					) : logs.length === 0 ? (
						<div className="text-center py-12 text-gray-500 dark:text-gray-400">
							No change log entries found
						</div>
					) : (
						<div className="overflow-x-auto">
							<table className="w-full text-sm">
								<thead>
									<tr className="border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
										<th className="py-3 px-3 text-left dark:text-gray-300">Time</th>
										<th className="py-3 px-3 text-left dark:text-gray-300">Product</th>
										<th className="py-3 px-3 text-left dark:text-gray-300">Type</th>
										<th className="py-3 px-3 text-left dark:text-gray-300">Field</th>
										<th className="py-3 px-3 text-left dark:text-gray-300">Old</th>
										<th className="py-3 px-3 text-left dark:text-gray-300">New</th>
										<th className="py-3 px-3 text-left dark:text-gray-300">Changed By</th>
										<th className="py-3 px-3 text-left dark:text-gray-300">Reason</th>
									</tr>
								</thead>
								<tbody>
									{logs.map((log: any) => (
										<tr key={log.id} className="border-b dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/50">
											<td className="py-2 px-3 text-xs dark:text-gray-400 whitespace-nowrap">
												{new Date(log.created_at).toLocaleString()}
											</td>
											<td className="py-2 px-3">
												<div className="dark:text-gray-200 text-xs max-w-[150px] truncate" title={log.product_name}>
													{log.product_name || `#${log.med_product_id}`}
												</div>
												<div className="text-xs text-gray-400">#{log.med_product_id}</div>
											</td>
											<td className="py-2 px-3">
												<Badge variant={changeTypeBadgeColor(log.change_type) as any} className="text-xs">
													{log.change_type}
												</Badge>
											</td>
											<td className="py-2 px-3 font-mono text-xs dark:text-gray-300">{log.field_name}</td>
											<td className="py-2 px-3 text-xs dark:text-gray-400 max-w-[100px] truncate" title={log.old_value}>
												{log.old_value || "—"}
											</td>
											<td className="py-2 px-3 text-xs font-medium dark:text-gray-200 max-w-[100px] truncate" title={log.new_value}>
												{log.new_value || "—"}
											</td>
											<td className="py-2 px-3 text-xs dark:text-gray-400 max-w-[150px] truncate">
												{log.changed_by_name || log.changed_by_email}
											</td>
											<td className="py-2 px-3 text-xs dark:text-gray-400 max-w-[150px] truncate" title={log.reason}>
												{log.reason || "—"}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</CardContent>
			</Card>

			{totalPages > 1 && (
				<div className="flex justify-center">
					<Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
				</div>
			)}
		</div>
	);
}
